import { useEffect, useState } from 'react';
import {
  getClubsByInstructor,
  getClubsByManager,
  getClubsByOwner,
} from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { useAuth } from '@/hooks/useAuth';
import type { Club } from '@/types/club.model';
import { handleGlobalError } from '@/utils/error-handler';

/**
 * Every club the current user can reach from the panel — owned, managed and
 * taught at — as a single list, deduplicated and sorted by name. Only the fields a
 * picker needs, plus the sport of each court so callers can tell what a club offers
 * without fetching it again; use `ClubList` when you need the full club rows.
 */
export function useMyClubs(): { clubs: Club[]; loading: boolean } {
  const {
    hasOwnerRole,
    hasManagerRole,
    hasInstructorRole,
    isFutureOwner,
  } = useAuth();
  const canSeeOwnedClubs = hasOwnerRole || isFutureOwner;

  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const fields = CLUB_FIELDS.COURT_SPORTS;
    Promise.all([
      canSeeOwnedClubs ? getClubsByOwner(fields) : [],
      hasManagerRole ? getClubsByManager(fields) : [],
      hasInstructorRole ? getClubsByInstructor(fields) : [],
    ])
      .then((results) => {
        if (cancelled) return;
        const byId = new Map<string, Club>();
        results.flat().forEach((club) => byId.set(club.id, club));
        setClubs(
          [...byId.values()].sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setClubs([]);
        handleGlobalError(error, 'Error', 'No se pudieron cargar tus clubes.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canSeeOwnedClubs, hasManagerRole, hasInstructorRole]);

  return { clubs, loading };
}
